// Dev-only fixtures + dispatcher for agent modes (plan 101 slice 06) — the org
// collection (`/v1/agent-modes`) and each repo's (`/v1/agent-modes/repo/:repoId`),
// the contract spelled in `./agent-modes.ts`.
//
// STATEFUL for the reason `system-prompts-mocks.ts` gives: the panel re-reads
// after every write, so a stateless mock would serve the pre-write row back and
// make every switch look dead. The resolution and the write validation are the
// DOMAIN's (`resolveAgentMode`, `AgentModeRowSchema`) — the same two the API
// runs — so a fixture cannot resolve or refuse differently from the server, and
// the 422 a story shows carries the domain's own sentence as its `detail`.

import {
  AGENT_MODE_TOOL_CEILINGS,
  AGENT_MODE_TOOL_FLOORS,
  AGENT_MODE_WRITABLE,
  type AgentModeKey,
  AgentModeKeySchema,
  AgentModeRowSchema,
  type AgentModeStoredRow,
  AgentModeWriteRefusedError,
  agentModeWritable,
  resolveAgentMode,
} from '@developerz/domain';
import { ApiError } from './api';

type Rows = Map<AgentModeKey, AgentModeStoredRow>;

/** The org rows. Seeded with one narrowing so the console shows an `org` source. */
const orgRows: Rows = new Map();
/** repoId → that repo's override rows. Starts empty: every repo inherits. */
const repoRows = new Map<string, Rows>();

function seed(): void {
  orgRows.clear();
  repoRows.clear();
  orgRows.set('scout', {
    mode_key: 'scout',
    enabled: true,
    code_tools: ['code.read', 'code.grep', 'code.glob'],
    revision: 2,
  });
}
seed();

const rowsOf = (repoId: string | null): Rows => {
  if (repoId === null) return orgRows;
  const existing = repoRows.get(repoId);
  if (existing) return existing;
  const fresh: Rows = new Map();
  repoRows.set(repoId, fresh);
  return fresh;
};

/** One mode as the API projects it: the resolution plus its ceiling and floor. */
function viewOf(key: AgentModeKey, repoId: string | null) {
  return {
    ...resolveAgentMode({
      key,
      orgRow: orgRows.get(key) ?? null,
      repoRow: repoId === null ? null : (repoRows.get(repoId)?.get(key) ?? null),
    }),
    ceiling: [...AGENT_MODE_TOOL_CEILINGS[key]],
    floor: [...AGENT_MODE_TOOL_FLOORS[key]],
  };
}

function list(repoId: string | null) {
  // The scope's WRITABLE keys, as the API lists them (#7008).
  const keys = repoId === null ? AGENT_MODE_WRITABLE.org : AGENT_MODE_WRITABLE.repo;
  return { data: keys.map((key) => viewOf(key, repoId)), next_cursor: null };
}

/** `SetAgentModeBodySchema.expect`, loosely — the mock reads it the way the route does. */
type MockExpect = { revision?: number; absent?: true } | undefined;

/** `ConfigLastActor`'s member shape (`@developerz/mcp`) — who won a stale race. */
type MockActor = { readonly user_id: string; readonly login: string | null };

/** Who "saved first" on a mock stale refusal. */
const MOCK_MODE_TEAMMATE: MockActor = { user_id: 'u_mock_teammate', login: 'grace' };

/** The #5208 409 `config-stale`, with the extensions `staleConflict` sends. */
function modeStale(current: AgentModeStoredRow | null): Promise<never> {
  const who = current === null ? 'it was cleared' : `saved by ${MOCK_MODE_TEAMMATE.login}`;
  const detail = `this agent mode changed since you read it (${who}); re-read it and retry`;
  return Promise.reject(
    new ApiError(409, 'config stale', undefined, detail, {
      reason: 'stale',
      current,
      last_actor: current === null ? null : MOCK_MODE_TEAMMATE,
    }),
  );
}

/** Whether `expect` refuses this write against the stored row — the route's own rule. */
function moved(expect: MockExpect, stored: AgentModeStoredRow | null): boolean {
  if (expect === undefined) return false;
  if (expect.absent === true) return stored !== null;
  return stored?.revision !== expect.revision;
}

function put(repoId: string | null, init?: RequestInit): Promise<unknown> {
  const body: { expect?: MockExpect; [key: string]: unknown } = init?.body
    ? JSON.parse(String(init.body))
    : {};
  // `AgentModeRowSchema` is `.strict()` over exactly `{mode_key, enabled, code_tools}` —
  // the domain shape the write door validates. `expect` (#5208) is transport-only on
  // the route (`SetAgentModeBodySchema` layers it beside the row), so the mock parses
  // the row alone, the same split `apps/api/src/routes/agent-modes.ts`'s `setInput` draws.
  const { expect: _expect, ...rowBody } = body;
  const parsed = AgentModeRowSchema.safeParse(rowBody);
  if (!parsed.success) {
    const detail = `agent mode refused: ${parsed.error.issues.map((i) => i.message).join('; ')}`;
    return Promise.reject(new ApiError(422, 'invalid agent mode', undefined, detail));
  }
  // The write matrix, refused with the db seam's own sentence (`writableRow`).
  const scope = repoId === null ? 'org' : 'repo';
  if (!agentModeWritable(scope, parsed.data.mode_key)) {
    const refused = new AgentModeWriteRefusedError(scope, parsed.data.mode_key);
    return Promise.reject(
      new ApiError(422, 'invalid agent mode', undefined, `agent mode refused: ${refused.message}`),
    );
  }
  const rows = rowsOf(repoId);
  const stored = rows.get(parsed.data.mode_key) ?? null;
  if (moved(body.expect, stored)) return modeStale(stored);
  const row: AgentModeStoredRow = { ...parsed.data, revision: (stored?.revision ?? 0) + 1 };
  rows.set(parsed.data.mode_key, row);
  return Promise.resolve({ mode: viewOf(parsed.data.mode_key, repoId) });
}

/** `{ repoId, key }` for an agent-modes address, or `null` when it names none. */
function addressOf(bare: string): { repoId: string | null; key: string | null } | null {
  const repo = /^\/v1\/agent-modes\/repo\/([^/]+)(?:\/([^/]+))?$/.exec(bare);
  if (repo) return { repoId: decodeURIComponent(repo[1] ?? ''), key: repo[2] ?? null };
  const org = /^\/v1\/agent-modes(?:\/([^/]+))?$/.exec(bare);
  if (org) return { repoId: null, key: org[1] ?? null };
  return null;
}

/** Serve both collections; `null` for anything else. */
export function agentModesMock(
  path: string,
  method: string,
  init?: RequestInit,
): Promise<unknown> | null {
  const at = addressOf(path.split('?')[0] ?? path);
  if (at === null) return null;
  if (method === 'GET' && at.key === null) return Promise.resolve(list(at.repoId));
  if (method === 'PUT' && at.key === null) return put(at.repoId, init);
  if (method === 'DELETE' && at.key !== null) {
    const key = AgentModeKeySchema.safeParse(decodeURIComponent(at.key));
    if (!key.success || !agentModeWritable(at.repoId === null ? 'org' : 'repo', key.data)) {
      return Promise.reject(new ApiError(404, 'no such mode'));
    }
    rowsOf(at.repoId).delete(key.data);
    return Promise.resolve(undefined);
  }
  return null;
}

/** Restore the pristine fixtures — the shared reset seam (`./mock-state.ts`). */
export function resetAgentModesMocks(): void {
  seed();
}
